// screens/Clark/answer/parts.jsx — Clark's answer rendering contract.
//
// Reusable, data-free components. B1b and B3 render real data through these; nothing
// here fetches, and nothing here knows where its props came from. Built from static
// props now, but shaped so real per-check events can drive the trace later.
//
// THE RULES THIS FILE ENFORCES, because they are product guarantees and not styling:
//
//   1. 🔴 NO CITATION RENDERS WITHOUT A RESOLVED STATUS. A cite with no status does not
//      fall back to plain text and does not show a spinner — it renders as the honest
//      unverified affordance. Silence is the one thing this surface may never do.
//   2. 🔴 SERIF IS THE VOICE OF THE LAW: QUOTED LAW *AND CASE NAMES*. Rule 2 used to read
//      "exactly one thing: quoted law", and BUNDLE E · RULE 5 widens it by exactly one
//      category, for a reason that is citation correctness rather than taste. Case names
//      rendered in MONOSPACE, which reads as code — and Indigo R2.1 requires case names
//      ITALICIZED. A monospace case name is not a styling preference, it is a citation
//      format defect. Serif italic satisfies R2.1 and matches how a brief reads.
//      Still forbidden: serif on headings, titles or emphasis.
//   3. Prose cites are LINK-STYLE TEXT — serif italic, accent, dotted underline,
//      superscript marker. BOX CHIPS appear only in case rows and the wide-tier table.
//   4. The treatment pill is VERDICT-ONLY. There is no as-of line: the citator verdict
//      shape carries no timestamp (goodlawLazy.js returns {status, treatment,
//      treatingCaseId, source, sweepVersion} — currency is a corpus-generation integer
//      with no calendar meaning), and inventing one would be a lie with a date on it.
//      "Good law" is never rendered — the engine cannot grant it from silence.
//   5. Status is never conveyed by COLOUR ALONE — every state carries text or an icon.

import { useState, useEffect, useRef } from "react";
// RUN OR — the sign-up gate on the Research link. See its header for the measured hole it closes.
import { blockResearchIfSignedOut } from "../researchGate.js";
// 🔴 BUNDLE E · LANE 2 — CLARK'S FEEDBACK ROW CONSUMES CITATIONS' STYLESHEET, IT DOES NOT COPY IT.
// The runbook asks for a feedback row "1:1 with Citations". Reproducing the rules here would make
// them 1:1 TODAY and drifting from tomorrow — the same vendored-copy failure RULE 6 is about, one
// scale down. Importing the real file makes them 1:1 BY CONSTRUCTION: Lane 3's centring of
// `Send report` is one edit in one file and it lands on both surfaces in the same commit.
// The markup below therefore uses Citations' `ccv2-fbk*` class names deliberately, not by accident.
import "../../MagicCite/FeedbackRow.css";

/* ─────────────────────────── treatment ─────────────────────────── */

// Verdict-only. The keys are goodlawLazy.js's mapEngineVerdict flags.
//
// 🔴 BUNDLE E · LANE 2 — `short` IS A SECOND SPELLING OF THE SAME FOUR STATES, AND IT LIVES HERE
// FOR THE SAME REASON THE FIRST ONE DOES. The Sources rail is 264px wide and "No negative
// treatment detected." wraps to three lines in it, so the rail needs a compact label. The one
// thing that must NOT happen is the rail inventing one — that is precisely the ge5 contradiction
// (the composer said "not yet analyzed" while the panel said "no negative treatment detected",
// about the same case in the same response). So the short form is part of the ONE vocabulary,
// keyed by the same flag, and `clarkAuthorityParity.test.js` still pins that no file but this one
// may spell any of it.
//
// The short forms say LESS, never something different: "No negative" is the same claim as "No
// negative treatment detected." with the noun elided, and neither is ever "good law".
export const TREATMENT_COPY = {
  "no-known-negative": { label: "No negative treatment detected.", short: "No negative", tone: "clear", glyph: "✓" },
  negative:            { label: "Negative treatment",              short: "Negative",    tone: "negative", glyph: "✕" },
  caution:             { label: "Caution — treatment noted",       short: "Caution",     tone: "caution", glyph: "!" },
  pending:             { label: "Treatment check pending",         short: "Not yet checked", tone: "pending", glyph: "…" },
};

/**
 * `compact` selects the short spelling. It changes the WORDS, never the state: the flag, the tone
 * and the glyph are identical, so a compact pill and a full pill can never disagree about a case.
 */
export function TreatmentPill({ flag, compact = false }) {
  // 🔴 A5.9/10 — AN EXPLICIT `null` MEANS "WE MAKE NO TREATMENT CLAIM", AND RENDERS NOTHING.
  // With the GoodLaw citator off the server sends no `goodLaw` field, `pillFlag` returns null, and
  // the honest rendering is no pill at all. This is NOT a hole in the moat rule below — that rule
  // governs an UNKNOWN verdict while the citator is running ("we checked, no answer yet"), and it
  // still applies to every other value. What it must not do is answer a question nobody asked: a
  // "Not yet analyzed" pill on a product that no longer analyses treatment is a promise, not a
  // status, and it is the more misleading of the two.
  if (flag === null) return null;
  // An unknown or missing flag is PENDING, never "clear" — the moat rule is that a
  // clean verdict is only ever granted by a completed exhaustive read, never inferred
  // from the absence of a signal.
  const t = TREATMENT_COPY[flag] || TREATMENT_COPY.pending;
  return (
    <span className="cc-tx-pill" data-tone={t.tone} data-compact={compact ? "1" : "0"} data-testid="clark-treatment">
      {/* The glyph is the non-colour channel — screen readers get the label, and a
          colour-blind reader gets a distinct mark, not just a different hue. */}
      <span className="cc-tx-pill-glyph" aria-hidden="true">{t.glyph}</span>
      {/* The compact pill shows the short word but keeps the FULL sentence as its accessible
          name, so a screen reader is never given the abbreviated claim. */}
      <span className="cc-tx-pill-label" aria-hidden={compact ? "true" : undefined}>
        {compact ? t.short : t.label}
      </span>
      {compact && <span className="cc-sr-only">{t.label}</span>}
    </span>
  );
}

/* ─────────────────────────── question echo — DELETED ─────────────────────────── */

// 🔴 BUNDLE E · LANE 2 — `QuestionEcho` IS GONE. It rendered a "YOU ASKED" card at the top of
// every answer, restating the question that is already in the user's own bubble directly above it,
// in a chat thread the user is looking at. It made sense when the answer was a standalone document
// (B2 built it before the thread existed); in a conversation it is the answer's first act being to
// repeat the question back.
//
// The question is NOT lost anywhere it is still doing work: the Sources rail heads its chronology
// with it (`SourcesColumn`'s TurnHeader), because there the rail genuinely needs to say WHICH turn
// its authorities belong to — a panel that re-points silently as the conversation moves is the
// defect Bundle C fixed. Deleted rather than left unused: an exported component nobody renders is
// the next person's twenty minutes.

/* ─────────────────────────── verification trace ─────────────────────────── */

/**
 * Deterministic checks, animating in mono, then collapsing to a pill that re-expands.
 *
 * `checks` is the event shape a real SSE stream emits one at a time:
 *   { id, label, detail?, status: 'pass' | 'fail' | 'running' }
 *
 * 🔴 B5 STEP 2 — `live` IS THE PROP CHANGE B2 PREDICTED. B2 wrote this "shaped so real per-check
 * events can drive the trace later" and fed it static props on a 260ms timer. Clark's stream now
 * emits a `check` event when the thing it describes actually happened, so:
 *
 *   live=false (the old behaviour) — the whole list is known up front and is revealed on a timer.
 *   live=true  (streaming)         — the list GROWS. Every check is revealed the instant it
 *                                    arrives, the timer is not used at all, and the trace does not
 *                                    collapse while work is still in flight.
 *
 * The distinction matters because the timer is a simulation of progress and the events are
 * progress. Running the timer over a growing array would restart the reveal on every arrival,
 * which reads as flicker rather than as work.
 */
// Default the preference from the OS rather than requiring every call site to pass it —
// a caller that forgets would silently animate for a user who asked us not to.
/* RUN TA — EXPORTED, not copied. ClarkAnswerV2's reveal needs the same preference, and this
   file's own header on the shared card says what a second implementation of one idea costs:
   "two implementations of one idea, free to disagree". A reveal that animated for a user who
   asked for no motion because the two copies drifted is exactly that disagreement. */
export function prefersReducedMotion() {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
}

/**
 * 🔴 STREAM C — THE ONE PROGRESS INDICATOR, AND WHY IT IS ONLY DOTS.
 *
 * ── THE DIAGNOSIS THE AUDIT ASKED FOR ────────────────────────────────────────────────────────
 * `.ccv2-clark-typing` "could not be produced even with 12 seconds of injected latency", and it is
 * not a threshold or a race. index.jsx gates the generic indicator on `sending && !streamingTurnId`
 * (Bundle C · Defect 4: one progress indicator per turn). `streamingTurnId` is derived from
 * `turns.find(t => t.answer?.streaming)`, and answerApi.js creates the live turn with
 * `{ streaming: true }` in the SAME tick the request goes out (answerApi.js:179). So on the
 * research path `streamingTurnId` is non-null from the first frame after send, and the dots are
 * suppressed for the entire turn — correctly, by that rule, because the streaming answer rendered
 * its own `VerificationTrace` instead.
 *
 * The audit ran with `CLARK_RETRIEVAL=1`, i.e. the research path, which is the ONLY path where the
 * dots are suppressed. They were never broken; they were unreachable on the path being tested.
 *
 * ── THE FIX ──────────────────────────────────────────────────────────────────────────────────
 * Not "un-suppress the dots" — that would restore the double indicator Defect 4 removed. The
 * research turn's indicator BECOMES the dots. `VerificationTrace` was the checklist the brief rules
 * out ("no checklist, no step list"), and it is off the live path entirely now.
 *
 * The markup is `.ccv2-clark-typing`'s, deliberately: one class, one keyframe, one appearance on
 * both paths, so the two can no longer diverge. `role="status"` + a visually-hidden label, because
 * three animated dots announce nothing to a screen reader.
 */
export function TypingDots({ label = "Clark is working on your answer" }) {
  return (
    <div className="ccv2-clark-typing" role="status" aria-live="polite" data-testid="clark-typing-dots">
      <span /><span /><span />
      <span className="cc-sr-only">{label}</span>
    </div>
  );
}

/**
 * ⚠️ STREAM C — NO LONGER RENDERED ON ANY LIVE PATH. The streaming research answer used to be this
 * component's only caller; it now renders `TypingDots`. What is left references it is the component
 * gallery (answer/gallery.jsx), which is a dev surface. Kept rather than deleted so the gallery
 * keeps building, but do not re-introduce it to an answer without re-reading the brief: a checklist
 * during synthesis was ruled out explicitly.
 */
export function VerificationTrace({ checks = [], stepMs = 260, live = false, reducedMotion = prefersReducedMotion() }) {
  const [revealed, setRevealed] = useState(reducedMotion || live ? checks.length : 0);
  const [collapsed, setCollapsed] = useState(false);
  const [userToggled, setUserToggled] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    // Live: the arrival IS the reveal. No interval — see the header.
    if (live) { setRevealed(checks.length); return undefined; }
    if (reducedMotion) { setRevealed(checks.length); return undefined; }
    setRevealed(0);
    let i = 0;
    timer.current = setInterval(() => {
      i += 1;
      setRevealed(i);
      if (i >= checks.length) clearInterval(timer.current);
    }, stepMs);
    return () => clearInterval(timer.current);
  }, [checks, stepMs, reducedMotion, live]);

  // Collapse once every check has landed — but never while work is still in flight, and never
  // fighting a user who opened it.
  useEffect(() => {
    if (live) return undefined;
    if (revealed >= checks.length && checks.length > 0 && !userToggled) {
      const t = setTimeout(() => setCollapsed(true), reducedMotion ? 0 : 420);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [revealed, checks.length, userToggled, reducedMotion, live]);

  const passed = checks.filter((c) => c.status === "pass").length;
  const failed = checks.filter((c) => c.status === "fail").length;

  if (collapsed) {
    return (
      <button
        type="button"
        className="cc-ans-trace-pill"
        data-testid="clark-trace-pill"
        data-failed={failed ? "1" : "0"}
        aria-expanded="false"
        onClick={() => { setUserToggled(true); setCollapsed(false); }}
      >
        <span className="cc-ans-trace-pill-glyph" aria-hidden="true">{failed ? "!" : "✓"}</span>
        {failed
          ? `${failed} of ${checks.length} checks failed`
          : `Verified — ${passed} check${passed === 1 ? "" : "s"} passed`}
      </button>
    );
  }

  return (
    <div className="cc-ans-trace" data-testid="clark-trace" aria-live="polite">
      <ol className="cc-ans-trace-list">
        {checks.slice(0, revealed).map((c) => (
          <li key={c.id} className="cc-ans-trace-row" data-status={c.status} data-testid="clark-trace-row">
            <span className="cc-ans-trace-glyph" aria-hidden="true">
              {c.status === "pass" ? "✓" : c.status === "fail" ? "✕" : "·"}
            </span>
            <span className="cc-ans-trace-label">{c.label}</span>
            {c.detail && <span className="cc-ans-trace-detail">{c.detail}</span>}
            {/* Text, not colour — a failed check says "failed". */}
            <span className="cc-sr-only">{c.status === "pass" ? "passed" : c.status === "fail" ? "failed" : "running"}</span>
          </li>
        ))}
      </ol>
      {/* Not offered while live: collapsing mid-stream would replace a running trace with a pill
          reading "Verified — N checks passed", which is a claim the turn has not yet earned. */}
      {!live && revealed >= checks.length && checks.length > 0 && (
        <button
          type="button"
          className="cc-ans-trace-collapse"
          onClick={() => { setUserToggled(true); setCollapsed(true); }}
          aria-expanded="true"
        >
          Collapse
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────── prose cite ─────────────────────────── */

/**
 * A citation INSIDE prose: link-style text, never a box.
 * `cite` = { marker, short, full, status, statusLabel }
 *
 * 🔴 If `status` is missing the cite renders as UNVERIFIED — visibly, with the honest
 * label — rather than as plain text or a spinner. A citation with no resolved status is
 * the exact thing this surface must never render silently.
 */
export function ProseCite({ cite, onCopy }) {
  const [copied, setCopied] = useState(false);
  const resolved = !!cite.status;
  const status = cite.status || "unverified";
  const statusLabel = cite.statusLabel || (resolved ? status : "Unverified — not confirmed against the engine");

  const copy = () => {
    navigator.clipboard?.writeText(cite.full || cite.short || "").catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
    onCopy?.(cite);
  };

  return (
    <button
      type="button"
      className="cc-ans-cite"
      data-status={status}
      data-testid="clark-prose-cite"
      onClick={copy}
      // Hover/focus shows the full Bluebook cite plus its status.
      title={`${cite.full}\n${statusLabel}`}
      aria-label={`${cite.full}. ${statusLabel}. Activate to copy.`}
    >
      <span className="cc-ans-cite-text">{cite.short}</span>
      <sup className="cc-ans-cite-marker" aria-hidden="true">{cite.marker}</sup>
      {copied && <span className="cc-ans-cite-copied" role="status">Copied</span>}
    </button>
  );
}

/* ─────────────────────────── authority block ─────────────────────────── */

// Parchment tint, serif italic for the quoted law, mono provenance beneath.
// This is the ONLY serif in the UI.
export function AuthorityBlock({ quote, provenance }) {
  return (
    <figure className="cc-ans-authority" data-testid="clark-authority-block">
      <blockquote className="cc-ans-authority-quote">{quote}</blockquote>
      <figcaption className="cc-ans-authority-prov">{provenance}</figcaption>
    </figure>
  );
}

/* ─────────────────────────── authorities panel — MOVED ─────────────────────────── */

// 🔴 BUNDLE 2 · STEP 4 — `AuthoritiesPanel` LIVED HERE AND NOW LIVES IN `./AuthorityCard.jsx`,
// as `AuthorityList`. It moved rather than being re-exported, so that there is exactly one import
// path to the authority vocabulary and no second name for the same component.
//
// The dependency runs ONE WAY on purpose: AuthorityCard.jsx imports `TreatmentPill` from this file
// (this is still the home of the treatment vocabulary), and this file imports nothing back. A
// re-export shim here would have made the two files mutually dependent for no benefit.

/* ─────────────────────────── answer actions ─────────────────────────── */

/**
 * 🔴 BUNDLE E · LANE 2 — TWO ACTIONS AND A FEEDBACK LINE, NOT FIVE CONTROLS IN ONE STRIP.
 *
 * B4 put the thumbs in the SAME row as the actions, reasoning that a feedback widget with its own
 * band reads as a survey. Two things were wrong with the result rather than with the reasoning:
 * the row carried FIVE controls of equal visual weight (Helpful, Not helpful, Send to Draft, Open
 * in Research, Copy all citations, Copy answer), so nothing in it was primary; and Citations —
 * the surface a user meets first — already had a settled feedback row with the question LEFT and
 * the controls RIGHT. Two surfaces, two answers to the same problem.
 *
 * Now: `Open in Research` is the primary and `Copy Answer` the secondary, and feedback gets its
 * own line beneath a hairline, in Citations' exact shape and Citations' exact stylesheet.
 *
 * 🔴 "Send to Draft" IS DELETED (runbook Lane 2: "No Send to Matter"). It was `onSendToDraft`,
 * which — like `onOpenResearch` — no caller in production ever passed, so the button flashed
 * "Sent" and sent nothing. A control that lies about having acted is worse than no control.
 * `onCopyCitations` goes with it: the rail is the citation list now, and every card there is a
 * link, so a bulk copy of strings duplicated it in a less useful form.
 *
 * 🔴 `Open in Research` IS LIVE AS OF BUNDLE F · LANE 2, AND IT IS AN ANCHOR, NOT A BUTTON.
 *
 * It was inert for three bundles, on purpose and correctly: Research's table had `/answer` (which
 * RE-RUNS the question), `/case/:id` and a `*` catch-all, so there was nowhere to send an answer,
 * and pointing it at a fresh run would have made the two surfaces disagree about the same question
 * in the same minute. Bundle F built `/research/answer/:id`, which renders the STORED payload —
 * one SELECT, no engine, no sweep — so the destination now exists.
 *
 * Three deliberate properties, each of which was a defect in the old shape:
 *
 *  1. **It is an `<a href>`.** The old control was a `<button onClick={() => onOpenResearch?.()}>`,
 *     which is how it managed to be inert: an optional handler nobody passed produced a control
 *     that looked identical whether or not it worked. An anchor cannot be silently disarmed — it
 *     either has a destination or it is not rendered. It also gets middle-click, cmd-click, "copy
 *     link" and a status-bar preview for free, all of which a lawyer wants for an answer.
 *  2. **No destination → NO CONTROL.** `storedAnswerHref(null)` is null and this renders nothing
 *     in its place. `answerId` is legitimately null for every uncached answer (a decline, an
 *     unknown ranker, a failed write). The rule this lane enforces is that the control appears
 *     exactly when it works.
 *  3. **`onOpenResearch` is kept as an OPTIONAL side-effect hook**, not as the navigation. If a
 *     caller passes it, it fires on click (for analytics or to close a panel) and navigation still
 *     happens via the href. Nothing depends on it being passed.
 *
 * 🔴 "Send to Draft" IS STILL DELETED and this is not a precedent to bring it back. It was removed
 * because it flashed "Sent" and sent nothing — the same disease, without a cure available.
 */
export function AnswerActions({ storedHref, onOpenResearch, onCopyAnswer, onFeedback }) {
  const [done, setDone] = useState("");
  const [voted, setVoted] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [reportText, setReportText] = useState("");
  const flash = (k, fn) => { fn?.(); setDone(k); setTimeout(() => setDone(""), 1400); };
  // The vote is recorded once and the control then states what was recorded, so a second click
  // cannot silently double-count.
  const vote = (verdict, freeformText) => {
    if (voted) return;
    setVoted(verdict);
    onFeedback?.(verdict, freeformText);
  };

  return (
    <>
      <div className="cc-ans-actions" data-testid="clark-actions">
        {/* Rendered only when there is a stored answer to open. See the header note (2). */}
        {storedHref && (
          /* 🔴 RUN OR — THE THIRD RENDER SITE OF THIS LINK, AND THE ONE THAT COULD NOT HAVE BEEN
             GUARDED BY ITS CALLER. The old handler discarded the event entirely, so no handler a
             caller passed could ever have called `preventDefault` on this anchor. The event is now
             forwarded, and the gate runs here regardless of what the caller does. */
          <a className="cc-ans-action cc-ans-action--primary" href={storedHref}
             onClick={(e) => { if (blockResearchIfSignedOut(e)) return; onOpenResearch?.(e); }}
             onAuxClick={(e) => { if (blockResearchIfSignedOut(e)) return; onOpenResearch?.(e); }}
             data-testid="clark-action-research">
            Open in Research
          </a>
        )}
        {/* Capital A. It copies THE ANSWER — a proper noun for the thing on screen. */}
        <button type="button" className="cc-ans-action"
                onClick={() => flash("answer", onCopyAnswer)} data-testid="clark-action-copyanswer">
          {done === "answer" ? "Copied" : "Copy Answer"}
        </button>
      </div>

      {/* 🔴 1:1 WITH CITATIONS — question LEFT, thumbs and Report RIGHT, on their own line.
          Same class names, same stylesheet (imported at the top of this file), same SVG paths as
          screens/MagicCite/FeedbackRow.jsx. The only difference is the noun in the question and
          the endpoint the verdict goes to, which is the only difference there should be. */}
      {onFeedback && (
        <div className={"ccv2-fbk" + (voted ? " ccv2-fbk--thanks" : "")}
             data-testid="clark-feedback" data-voted={voted || ""}>
          {voted ? (
            /* Citations puts BOTH classes on the root in its thanks state
               (`className="ccv2-fbk ccv2-fbk--thanks"`), so the modifier is applied the same way
               here rather than on a nested div — otherwise the two rows would resolve the same
               rules through different structures, which is how "1:1" quietly stops being true. */
            <span className="ccv2-fbk-thanks-text" role="status">Thanks for the feedback.</span>
          ) : (
            <>
              <div className="ccv2-fbk-top">
                <span className="ccv2-fbk-prompt">Was this answer correct?</span>
                <div className="ccv2-fbk-actions">
                  {/* Stroke icons in currentColor. NO EMOJI: an emoji thumb is a font-dependent
                      raster that ignores the theme and cannot take a focus colour. */}
                  <button type="button" className="ccv2-fbk-btn"
                          title="Yes, this is correct" aria-label="This answer was correct"
                          onClick={() => vote("up")} data-testid="clark-feedback-up">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7.5V13a1 1 0 0 0 1 1h7.2a1 1 0 0 0 1-.7l1.5-5a1 1 0 0 0-1-1.3H9V4.2c0-.7-.5-1.2-1.2-1.2-.3 0-.6.2-.7.5L5 7.5H3z" /></svg>
                  </button>
                  <button type="button" className="ccv2-fbk-btn"
                          title="No, this is wrong" aria-label="This answer was not correct"
                          onClick={() => vote("down")} data-testid="clark-feedback-down">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M13 8.5V3a1 1 0 0 0-1-1H4.8a1 1 0 0 0-1 .7L2.3 7.7a1 1 0 0 0 1 1.3H7v2.8c0 .7.5 1.2 1.2 1.2.3 0 .6-.2.7-.5L11 8.5h2z" /></svg>
                  </button>
                  <button type="button" className={"ccv2-fbk-report" + (reporting ? " active" : "")}
                          title="Report a problem" aria-expanded={reporting}
                          onClick={() => setReporting((r) => !r)} data-testid="clark-feedback-report">
                    Report
                  </button>
                </div>
              </div>

              {reporting && (
                <div className="ccv2-fbk-reportbox">
                  <textarea
                    className="ccv2-fbk-reason"
                    rows={2}
                    value={reportText}
                    placeholder="What's wrong with this answer? (optional)"
                    onChange={(e) => setReportText(e.target.value)}
                    aria-label="What's wrong with this answer?"
                    data-testid="clark-feedback-report-text"
                  />
                  {/* A problem report is a negative signal — same reading as Citations. */}
                  <button type="button" className="ccv2-fbk-report-send"
                          onClick={() => vote("down", reportText)} data-testid="clark-feedback-report-send">
                    Send report
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

/* ─────────────────────────── the over-block state ─────────────────────────── */

/**
 * The designed refusal. This is not an error state — it is the honest failure the whole
 * product rests on, and it gets the same care as a success. It never renders a citation
 * Clark could not verify; it names what it could not do and offers what it CAN confirm.
 *
 * `candidates` = [{ id, cite, detail }]
 *
 * 🔴 B3: `headline`, `noCandidates` and `label` are OPTIONAL OVERRIDES, and they exist because a
 * probe caught this component saying the wrong thing. B2 built it for the CITATION over-block, so
 * its copy is "I can't verify this citation — <query>." B3 reuses it as the research decline —
 * correctly, it is the same designed refusal — but a user who asked "what is the law of X" was then
 * told their QUESTION was an unverifiable citation. Defaults are unchanged, so the citation path is
 * byte-identical; only a caller that passes an override sees different words.
 */
export function OverBlock({
  query, candidates = [], onPick, onDismiss,
  headline = "I can’t verify this citation",
  noCandidates = "Nothing in the corpus matches closely enough to offer. Adding a year, a court, or a second party name usually resolves it.",
  label = "Citation could not be verified",
}) {
  return (
    <div className="cc-ans-overblock" data-testid="clark-overblock" role="group" aria-label={label}>
      <p className="cc-ans-overblock-head">
        <span className="cc-ans-overblock-glyph" aria-hidden="true">!</span>
        {headline}{query ? <> — <span className="cc-ans-overblock-q">{query}</span></> : null}.
      </p>
      {candidates.length > 0 ? (
        <>
          <p className="cc-ans-overblock-sub">Did you mean…</p>
          <ul className="cc-ans-overblock-list" role="list">
            {candidates.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className="cc-ans-overblock-pick"
                  onClick={() => onPick?.(c)}
                  data-testid="clark-overblock-candidate"
                >
                  <span className="cc-ans-overblock-cite">{c.cite}</span>
                  {c.detail && <span className="cc-ans-overblock-detail">{c.detail}</span>}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        // No candidates is still an answer — it is NOT a blank.
        <p className="cc-ans-overblock-sub">{noCandidates}</p>
      )}
      {onDismiss && (
        <button type="button" className="cc-ans-overblock-dismiss" onClick={onDismiss}>Dismiss</button>
      )}
    </div>
  );
}
